import { cloneDeep, groupBy, sortBy, uniq } from 'lodash';
import { CalculateBags, RoundAcres, RoundPrice, RoundPricePer, RoundSeedingRate, RoundTargetYield, FixFloatingPointDrift as FixDrift } from '../CalculationUtilities';
import { mapObject } from '../mapObject';
import { IBagSizeProvider } from './BagSizeProvider';
import { IPricingProvider } from './PricingProvider';
import { IProductRollupCalcLogger, RelatedObjects } from './ProductRollupCalcLogger';

/**
 * Calculates the total prices and rates for a set of products. See Rollup and RollupRates types for more details on which values are totaled.
 * Designed to match the backend. 
 */
export class ProductRollupCalc 
{
	private _pricer: IPricingProvider;
	private _bagSizer: IBagSizeProvider;
	private _logger: IProductRollupCalcLogger;

	public constructor(pricer: IPricingProvider, bagSizer: IBagSizeProvider, logger?: IProductRollupCalcLogger)	
	{
		this._pricer = pricer;
		this._bagSizer = bagSizer;
		this._logger = logger;
	}
	
	public calculate(lines: ICalcProductLine[])
	{
		this.trace('Begin calculate', { lines, });

		// Calculate the line totals for each line.
		const interimLines: InterimLine[] = lines.map(l => this.calculateLine(l));

		// Total the values for each unique product.
		const productRollup = mapObject(
			groupBy(interimLines, l => l.key), 
			(lines, key) => 
			{
				const firstLine = lines[0];
				return {
					...this.totalLines(lines),
					cropId: firstLine.cropId,
					hybridId: firstLine.hybridId,
					treatmentId: firstLine.treatmentId,
				};
			},
		);

		// Total the values for each crop.
		const cropRollup: Record<string, Rollup> = {};
		for (const key of Object.keys(productRollup))
		{
			const product = productRollup[key];
			if (!cropRollup[product.cropId]) 
				cropRollup[product.cropId] = productRollup[key].totals;
			else
				cropRollup[product.cropId] = addRollups(cropRollup[product.cropId], productRollup[key].totals);
		}

		// Use the crop rollup to derive the overall plan rollup.
		const planRollup = Object.values(cropRollup).reduce(
			(acc, cur) => addRollups(acc, cur), 
			{
				totalCost: 0,
				discountedTotalCost: 0,
				totalDiscounts: 0,
				area: 0,
				totalBags: 0,
				requiredSeeds: 0,
				targetYield: 0,
			}
		);

		const returnedRollups = {
			productRollup,
			cropRollup: mapObject(cropRollup, (rollup, cropId) => ({...rollup, ...this.calculateRates(rollup), })),
			planRollup: {
				...planRollup,
				...this.calculateRates(planRollup),
			},
		};

		// Find all products without pricing data for reporting purposes.
		const missingPrices = lines.filter(l => !this._pricer.price(l));
		
		this.trace('End calculate', { returnedRollups, missingPrices, });

		return returnedRollups;
	}

	/** Calculate the actual totals from any subset of the interim lines. */
	private totalLines(lines: InterimLine[]): { 
		totals: Rollup & RollupRates, 
		preciseTotals: Rollup, 
		lines: InterimLine[]
	}
	{
		const roundedTotals:Rollup = {
			totalCost: 0,
			discountedTotalCost: 0,
			totalDiscounts: 0,
			area: 0,
			totalBags: 0,
			requiredSeeds: 0,
			targetYield: 0,
		};
		const preciseTotals:Rollup  = {
			totalCost: 0,
			discountedTotalCost: 0,
			totalDiscounts: 0,
			area: 0,
			totalBags: 0,
			requiredSeeds: 0,
			targetYield: 0,
		};
	
		for (const line of lines) 
		{
			roundedTotals.totalCost = FixDrift(roundedTotals.totalCost + line.rounded.totalCost);
			roundedTotals.discountedTotalCost = FixDrift(roundedTotals.discountedTotalCost + line.rounded.discountedTotalCost);
			roundedTotals.totalDiscounts = FixDrift(roundedTotals.totalDiscounts + line.rounded.totalDiscounts);
			roundedTotals.area = FixDrift(roundedTotals.area + line.rounded.area);
			roundedTotals.requiredSeeds = FixDrift(roundedTotals.requiredSeeds + line.rounded.requiredSeeds);
			roundedTotals.targetYield = FixDrift(roundedTotals.targetYield + line.rounded.targetYield);
			roundedTotals.totalBags = FixDrift(roundedTotals.totalBags + line.rounded.totalBags);
	
			preciseTotals.totalCost = FixDrift(preciseTotals.totalCost + line.precise.totalCost);
			preciseTotals.discountedTotalCost = FixDrift(preciseTotals.discountedTotalCost + line.precise.discountedTotalCost);
			preciseTotals.totalDiscounts = FixDrift(preciseTotals.totalDiscounts + line.precise.totalDiscounts);
			preciseTotals.area = FixDrift(preciseTotals.area + line.precise.area);
			preciseTotals.requiredSeeds = FixDrift(preciseTotals.requiredSeeds + line.precise.requiredSeeds);
			preciseTotals.targetYield = FixDrift(preciseTotals.targetYield + line.precise.targetYield);
			preciseTotals.totalBags = FixDrift(preciseTotals.totalBags + line.precise.totalBags);
		}
	
		// The rounding introduced error, adjust the lines to make the rounded total make sense.
		const adjustedLines = cloneDeep(lines);
		const priceDelta = FixDrift(roundedTotals.totalCost - preciseTotals.totalCost);
		const costDelta = FixDrift(roundedTotals.discountedTotalCost - preciseTotals.discountedTotalCost);
		const discountDelta = FixDrift(roundedTotals.totalDiscounts - preciseTotals.totalDiscounts);
		
		// TODO: Why am I getting a delta of .005?
		if (priceDelta !== 0) 
		{
			// Arbitrarily pick the line with the most money as the one to adjust.
			const line = sortBy(adjustedLines, l => l.rounded.totalCost).reverse()[0];
			line.rounded.totalCost += priceDelta;
	
			this.trace('Adjusted price', { priceDelta, line, });
		}
	
		if (costDelta !== 0) 
		{
			const line = sortBy(adjustedLines, l => l.rounded.discountedTotalCost).reverse()[0];
			line.rounded.discountedTotalCost += costDelta;
	
			this.trace('Adjusted totalCost', { costDelta, line, });
		}
	
		if (discountDelta !== 0) 
		{
			const line = sortBy(adjustedLines, l => l.rounded.totalDiscounts).reverse()[0];
			line.rounded.totalDiscounts += discountDelta;
					
			this.trace('Adjusted discount', { discountDelta, line, });
		}
	
		// Pass through the per bag price if we already have it, otherwise calculate it.
		const samePrice = adjustedLines.every(l => l.pricing.price === adjustedLines[0].pricing.price && l.pricing.pricingUnit === adjustedLines[0].pricing.pricingUnit);
		const pricing = adjustedLines[0].pricing;
		if (samePrice)
		{
			this.trace('Same price', { pricing });
		}

		// Add summaries to the totals.
		const rates = this.calculateRates(roundedTotals);
		
		return {
			lines: adjustedLines,
			totals: {
				...roundedTotals,
				...rates,
				avgCostPerAcre: samePrice && pricing.pricingUnit === 'Acre' ? pricing.price : rates.avgCostPerAcre,
				avgCostPerBag: samePrice && pricing.pricingUnit === 'Bag' ? pricing.price : rates.avgCostPerBag,
			},
			preciseTotals,
		};
	}

	/** Use the totals to derive the overall rates of things. */
	private calculateRates(rollup:Rollup): RollupRates
	{
		return {
			avgCostPerAcre: RoundPricePer(rollup.totalCost, rollup.area),
			avgCostPerBag: RoundPricePer(rollup.totalCost, rollup.totalBags),
			avgDiscountedCostPerAcre: RoundPricePer(rollup.discountedTotalCost, rollup.area),
			avgDiscountedCostPerBag: RoundPricePer(rollup.discountedTotalCost, rollup.totalBags),
			avgDiscountPerAcre: RoundPricePer(rollup.totalDiscounts, rollup.area),
			avgTargetYield: RoundTargetYield(rollup.targetYield / rollup.area),
			avgSeedingRate: RoundSeedingRate(rollup.requiredSeeds, rollup.area),
		};
	}

	/** Do all calculations for a single product line. This includes adding total price, bags, etc. */
	public calculateLine(line: ICalcProductLine): ICalcProductLine & {
		/** A unique key for the product line. */
		key: string,
		pricing: ICalcPricing,
		/** Principle values to be used in further calculations and in the UI. */
		rounded: Rollup,
		/** For debugging only. */
		precise: Rollup,
	}
	{
		const acres = line.appliedAcres;
		const roundedAcres = RoundAcres(line.appliedAcres);

		// Allow callers to set the calculated values for a line if needed. This only exists to support places where we have
		// a calculated value (like bags), but not the value needed to calculated it (seeds).
		const seeds = line.calculationOverrides?.seeds ?? FixDrift(line.seedingRate * roundedAcres);
		const roundedSeeds = line.calculationOverrides?.seeds ?? Math.ceil(seeds);

		const totalYield = line.calculationOverrides?.totalYield ?? FixDrift(line.targetYield * roundedAcres);
		const roundedTotalYield = line.calculationOverrides?.totalYield ?? RoundTargetYield(line.targetYield * roundedAcres);

		const bagSize = this._bagSizer.bagSize(line);

		const bags = line.calculationOverrides?.bags ?? FixDrift(seeds / bagSize);
		const roundedBags = line.calculationOverrides?.bags ?? CalculateBags(seeds, bagSize); 

		let pricing = this._pricer.price(line);
		// If no pricing info can be found assume 0, but also report the error.
		if (!pricing) 
		{
			pricing = {
				price: 0,
				pricingUnit: 'Acre',
			};
			this.trace('Price not found', { line, });
		} 

		const cost = this.calculateTotalCost(pricing, line.appliedAcres, bags);

		const roundedCost = mapObject(
			// Calc using rounded values.
			this.calculateTotalCost(pricing, roundedAcres, roundedBags), 
			// Round the results. Assume all values are prices.
			(price, k) => RoundPrice(price)
		);

		return {
			key: productKey(line),
			...line,
			pricing,
			precise: {
				totalCost: cost.totalCost,
				totalDiscounts: cost.totalDiscounts,
				discountedTotalCost: cost.discountedTotalCost,
				area: acres,
				requiredSeeds: seeds,
				targetYield: totalYield,
				totalBags: bags,
			},
			rounded: {
				totalCost: roundedCost.totalCost,
				totalDiscounts: roundedCost.totalDiscounts,
				discountedTotalCost: roundedCost.discountedTotalCost,
				area: roundedAcres,
				requiredSeeds: roundedSeeds,
				targetYield: roundedTotalYield,
				totalBags: roundedBags,
			},
		};
	}

	/** Calculate the total cost of a line */
	private calculateTotalCost(pricing: ICalcPricing, acres: number, bags: number)
	{
		const unknownUnit = [pricing.pricingUnit, pricing.cropDiscount?.unit, pricing.productDiscount?.unit]
			.filter(Boolean)
			.find(unit => !['Acre', 'Bag'].includes(unit));
		if (unknownUnit)
		{
			throw new Error(`Unable to hanldle pricing unit: ${unknownUnit}`);
		}

		const totalCost = {
			'Acre': FixDrift(acres * pricing.price),
			'Bag': FixDrift(bags * pricing.price),
		}[pricing.pricingUnit] ?? 0;

		const cropDiscount = (pricing.cropDiscount ? {
			'Acre': FixDrift(acres * pricing.cropDiscount.discount),
			'Bag': FixDrift(bags * pricing.cropDiscount.discount),
		}[pricing.cropDiscount.unit] : 0) ?? 0;

		const productDiscount = (pricing.productDiscount ? {
			'Acre': FixDrift(acres * pricing.productDiscount.discount),
			'Bag': FixDrift(bags * pricing.productDiscount.discount),
		}[pricing.productDiscount.unit] : 0) ?? 0;

		const costs = {
			totalCost,
			// Don't go below 0.
			discountedTotalCost: Math.max(FixDrift(totalCost - (cropDiscount + productDiscount)), 0),
			totalDiscounts: FixDrift(cropDiscount + productDiscount),

			cropDiscount,
			productDiscount,
		};

		const unitsMatch = [pricing.pricingUnit, pricing.cropDiscount?.unit, pricing.productDiscount?.unit]
			.filter(Boolean)
			.every(u => u === u[0]);
		if (!unitsMatch)
			this.trace('Pricing units and discount units did not match.', { pricing, acres, bags, costs, });

		return costs;
	}

	private trace(message: string, relatedObjects?: RelatedObjects) 
	{
		if (!this._logger) return;

		this._logger.trace(message, relatedObjects);
	}
}

/** 
 * Totals for products. 
 * 
 * Can represent:
 *  Values aggregated across multiple product lines.
 *  Calculated values in a single product line. 
 */
export type Rollup = {
	/** Total price before discounts. */
	totalCost: number;
	/** Total price after discounts. */
	discountedTotalCost: number;
	/** Total discounts. */
	totalDiscounts: number;
	/** Area this total applies to. aka pricing area. In acres. */
	area: number;
	/** Total number of bags of seed needed. */
	totalBags: number;
	/** Total number of seeds required. Not seeding rate. */
	requiredSeeds: number;
	/** Expected / Target yield per acre. buschels per acre. */
	targetYield: number; 
};

/** Values derived after aggregating multiple product lines into a rollup. */
export type RollupRates = {
	avgCostPerAcre: number;
	avgCostPerBag: number;
	avgDiscountedCostPerAcre: number;
	avgDiscountedCostPerBag: number;
	avgDiscountPerAcre: number;
	/** Average target yield in bu/acre. */
	avgTargetYield: number;
	/** Average seeds per acre. */
	avgSeedingRate: number;
}

/** Add 2 rollups together. */
function addRollups(a: Rollup, b: Rollup): Rollup 
{
	return {
		totalCost: FixDrift(a.totalCost + b.totalCost),
		discountedTotalCost: FixDrift(a.discountedTotalCost + b.discountedTotalCost),
		totalDiscounts: FixDrift(a.totalDiscounts + b.totalDiscounts),
		area: FixDrift(a.area + b.area),
		totalBags: FixDrift(a.totalBags + b.totalBags),
		requiredSeeds: FixDrift(a.requiredSeeds + b.requiredSeeds),
		targetYield: FixDrift(a.targetYield + b.targetYield),
	};
}

/** How this calculator expects products to be passed in. */
interface ICalcProductLine 
{
	hybridId: string;
	treatmentId?: string;
	cropId: string;

	/** Expected / Target yield per acre. buschels per acre. */
	targetYield: number;
	/** Area this product is applied to. In acres. */
	appliedAcres: number;
	/** Seeds per acre. */
	seedingRate: number;

	/** Overrides for calculation. If these values are provided, then they will not be calculated from the above parameters. */
	calculationOverrides?: {
		seeds?: number;
		totalYield?: number;
		bags?: number;
	}
}

/** Line with calculated values. */
type InterimLine = ICalcProductLine & {
	key: string;
	pricing: ICalcPricing;
	rounded: Rollup;
	precise: Rollup;
};


/** Details needed to uniquely identify a single product. */
export interface ICalcProduct
{
	cropId: string; 
	hybridId: string; 
	/** aka Treatment Code */
	treatmentId?: string; 
}

export interface ICalcPricing
{
	price: number;
	pricingUnit: CalcPricingUnit;
	cropDiscount?: CalcDiscount;
	productDiscount?: CalcDiscount;
}

export type CalcDiscount = {
	discount: number;
	unit: CalcPricingUnit;
}

export type CalcPricingUnit = 'Bag' | 'Acre';

/** Generate a unique key for a product. */
export function productKey({cropId, hybridId, treatmentId}: ICalcProduct)
{
	return `${cropId}-${hybridId}-${treatmentId ?? ''}`;
}